import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useLoading } from './LoadingContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, getToken } = useAuth();
  const { track } = useLoading();

  const fetchCartItems = useCallback(async (options = {}) => {
    const { showGlobalLoader = false } = options;
    try {
      setLoading(true);
      const token = getToken(false); // E-commerce token
      if (!token) return;

      const runner = async () => {
        const API_BASE = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API_BASE}/api/cart`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (data.success) {
          setCartItems(data.data);
        }
      };

      if (showGlobalLoader) {
        await track(runner);
      } else {
        await runner();
      }
    } catch (error) {
      console.error('Error fetching cart items:', error);
    } finally {
      setLoading(false);
    }
  }, [getToken, track]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCartItems({ showGlobalLoader: true });
    } else {
      setCartItems([]);
    }
  }, [fetchCartItems, isAuthenticated]);

  const addToCart = async (productId, quantity = 1, variantId = null) => {
    try {
      const token = getToken(false); // E-commerce token
      if (!token) {
        throw new Error('Please login to add items to cart');
      }

  const API_BASE = process.env.REACT_APP_API_URL || '';
  const response = await fetch(`${API_BASE}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: productId,
          variant_id: variantId,
          quantity: quantity
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchCartItems(); // Refresh cart
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { success: false, message: error.message };
    }
  };

  const updateCartItem = async (cartItemId, quantity) => {
    try {
      const token = getToken(false); // E-commerce token
  const API_BASE = process.env.REACT_APP_API_URL || '';
  const response = await fetch(`${API_BASE}/api/cart/${cartItemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ quantity })
      });

      const data = await response.json();
      if (data.success) {
        await fetchCartItems(); // Refresh cart
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
      return { success: false, message: error.message };
    }
  };

  const removeFromCart = async (cartItemId) => {
    try {
      console.log('🗑️ [Frontend] removeFromCart called with cartItemId:', cartItemId, 'type:', typeof cartItemId);
      const token = getToken(false); // E-commerce token
  const API_BASE = process.env.REACT_APP_API_URL || '';
  const url = `${API_BASE}/api/cart/${cartItemId}`;
      console.log('🗑️ [Frontend] DELETE request URL:', url);
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('🗑️ [Frontend] DELETE response status:', response.status);

      const data = await response.json();
      if (data.success) {
        await fetchCartItems(); // Refresh cart
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      return { success: false, message: error.message };
    }
  };

  const clearCart = async () => {
    try {
      const token = getToken(false); // E-commerce token
  const API_BASE = process.env.REACT_APP_API_URL || '';
  const response = await fetch(`${API_BASE}/api/cart`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setCartItems([]);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, message: error.message };
    }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      // line_total accounts for promotions like Buy One Get One Free (see
      // backend/services/bogoPricing.js) - falls back to price * quantity for any
      // cart response that doesn't include it yet.
      const lineTotal = item.line_total != null ? Number(item.line_total) : parseFloat(item.price) * item.quantity;
      return total + lineTotal;
    }, 0);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cartItems,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    fetchCartItems,
    getCartTotal,
    getCartItemsCount,
    cartTotal: getCartTotal(),
    cartItemsCount: getCartItemsCount()
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
